import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import "./IndividualProfile.scss";
import { FiAnchor, FiUmbrella } from "react-icons/fi";
import { formatCurrency } from "utils/number";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import { AccountClosePending, AccountClosed } from "statics/states";
import { CloseAccountType } from "statics/closeAccountTypes";

import Button from "components/Button";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";

import { closeAndLiquidateAccount, getUserState } from "actions/userActions";
import { toast } from "react-toastify";
import {
  setAccountForClose,
  setAccountPendingClosure,
} from "actions/accountActions";
import type { IraAccount } from "types/models";
import type { PendingClosure, RootState } from "types/state";

const mapStateToProps = (state: RootState) => ({
  iraAccounts: state.accounts.iraAccounts,
  closeAccountState: state.accounts.closeAccount,
  pendingClosures: state.accounts.pendingClosures,
});

const mapDispatchToProps = {
  closeAndLiquidateAccount,
  getUserState,
  push,
  setAccountForClose,
  setAccountPendingClosure,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

class CloseAccount extends React.PureComponent<Props> {
  isAccountPendingClosure = (account: IraAccount) => {
    // Check if account has pending closure in Redux
    const hasPendingClosure =
      (account.id as string) in this.props.pendingClosures;

    // Check if account state is AccountClosePending
    const isPendingClose = account.lifecycleState === AccountClosePending;

    return hasPendingClosure || isPendingClose;
  };

  getButtonState = (account: IraAccount) => {
    if (this.isAccountPendingClosure(account)) {
      return {
        label: "Close Pending",
        disabled: true,
      };
    }

    if (account.lifecycleState === AccountClosed) {
      return {
        label: "Account Closed",
        disabled: true,
      };
    }

    return {
      label: "Close Account",
      disabled: false,
    };
  };

  handleCloseAccount = (account: IraAccount) => {
    // balances is Maybe<AccountBalances>; original derefs with `.` (throws on
    // missing) — `!` preserves that. The float value is Maybe<number>.
    const balance = account.balances!.totalAccountBalance as number;
    const CLOSURE_FEE = 35.0;

    this.props.setAccountForClose(
      account.id as string,
      account.accountType as string
    );

    if (balance <= CLOSURE_FEE) {
      // Directly close account without going through withdrawal workflow
      const closeAccountType =
        balance > 0
          ? CloseAccountType.USER_INITIATED_WITH_FUNDS_BELOW_FEE_AMOUNT
          : CloseAccountType.USER_INITIATED_WITHOUT_FUNDS;

      this.props
        .closeAndLiquidateAccount(this.props.client as ApolloClient<object>, {
          accountId: account.id as string,
          closeAccountType,
        })
        // dispatchFullLifecycle resolves to a success {data} OR failure
        // {error} action; the original code reads both off the result.
        .then((result: { error?: string; data?: unknown }) => {
          if (result.error) {
            throw new Error(result.error);
          }
          if (!result.data) {
            throw new Error("No response data received from server");
          }
          this.props.setAccountPendingClosure(
            account.id as string,
            account.accountType as string
          );
          toast.success("Successfully submitted account closure request.");
        })
        .catch((error: { graphQLErrors?: Array<{ message?: string }> }) => {
          console.error("Account closure error:", error);
          // Check if it's a GraphQL error
          if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            toast.error(
              error.graphQLErrors[0].message ||
                "Failed to close account. Please try again."
            );
          } else {
            toast.error(
              "There was an error closing the account. Please try again."
            );
          }
        });
    } else {
      // For accounts with balance > $35, go through the workflow
      this.props.push("/dashboard/support/close-account");
    }
  };

  componentDidMount() {
    // Load pending closures from sessionStorage on mount
    const storedClosures = sessionStorage.getItem("pendingClosures");
    if (storedClosures) {
      const closures = JSON.parse(storedClosures) as Record<
        string,
        PendingClosure
      >;
      Object.entries(closures).forEach(([accountId, data]) => {
        this.props.setAccountForClose(accountId, data.accountType);
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    // Save pending closures to sessionStorage when they change
    if (prevProps.pendingClosures !== this.props.pendingClosures) {
      sessionStorage.setItem(
        "pendingClosures",
        JSON.stringify(this.props.pendingClosures)
      );
    }
  }

  renderAccountList = () => {
    const { iraAccounts = [] } = this.props;

    return iraAccounts.map((account) => {
      // balances is Maybe<AccountBalances>; original derefs with `.` (throws on
      // missing) — `!` preserves that. The float value is Maybe<number>.
      const balance = account.balances!.totalAccountBalance as number;
      const CLOSURE_FEE = 35.0;
      const showWarning = balance > 0 && balance <= CLOSURE_FEE;
      const showZeroBalanceConfirmation = balance === 0;
      const buttonState = this.getButtonState(account);

      const popover = showWarning ? (
        <Popover className="close-account-warning-popover">
          <Popover.Body>
            <div className="warning-content">
              <p>
                The remaining balance in your account (${balance.toFixed(2)})
                will be used to cover the account closure fee (up to $35).
              </p>
              <div className="warning-actions">
                <Button
                  btnLabel="Cancel"
                  name="cancel"
                  color="cancel"
                  onClick={() => document.body.click()}
                />
                <Button
                  btnLabel="Proceed"
                  name="submit"
                  color="red"
                  onClick={() => {
                    this.handleCloseAccount(account);
                    document.body.click();
                  }}
                />
              </div>
            </div>
          </Popover.Body>
        </Popover>
      ) : showZeroBalanceConfirmation ? (
        <Popover className="close-account-warning-popover">
          <Popover.Body>
            <div className="warning-content">
              <p>
                Are you sure you want to close this account? This action cannot
                be undone.
              </p>
              <div className="warning-actions">
                <Button
                  btnLabel="Cancel"
                  name="cancel"
                  color="cancel"
                  onClick={() => document.body.click()}
                />
                <Button
                  btnLabel="Close Account"
                  name="submit"
                  color="red"
                  onClick={() => {
                    this.handleCloseAccount(account);
                    document.body.click();
                  }}
                />
              </div>
            </div>
          </Popover.Body>
        </Popover>
      ) : null;

      return (
        <div
          key={account.id}
          className="account-container"
          style={{
            display: "flex",
            alignItems: "center",
            maxWidth: 600,
            marginBottom: 16,
            border: "1px solid #e0e0e0",
            borderRadius: 4,
            padding: 20,
            background: "#fff",
            marginLeft: 0,
            marginRight: 0,
          }}
        >
          <div
            className="account-item"
            style={{
              display: "flex",
              alignItems: "center",
              flex: 2,
              minWidth: 0,
            }}
          >
            <div>
              <div className="account-circle">
                {account.accountType === "TRADITIONAL_IRA" ? (
                  <FiAnchor size={16} color="#0066B2" />
                ) : (
                  <FiUmbrella size={16} color="#0066B2" />
                )}
              </div>
            </div>
            <div
              className="item-description"
              style={{
                paddingLeft: 15,
                paddingTop: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <p
                className="title"
                style={{
                  margin: 0,
                  fontWeight: 500,
                  fontSize: 14,
                  color: "#32325d",
                }}
              >
                {
                  accountTypesToEnglishMapping[
                    // accountType is Maybe<string>; unknown/missing keys
                    // resolve to undefined at runtime — cast for the index.
                    account.accountType as keyof typeof accountTypesToEnglishMapping
                  ]
                }
              </p>
              <p
                className="description"
                style={{ margin: 0, height: 0, fontSize: 0 }}
              ></p>
            </div>
          </div>
          <div
            style={{
              flex: "0 0 120px",
              minWidth: 90,
              textAlign: "left",
              paddingLeft: 10,
            }}
          >
            <p
              className="account-balance"
              style={{
                margin: 0,
                fontWeight: 500,
                fontSize: 14,
                color: "#32325d",
              }}
            >
              {formatCurrency(
                account.balances!.totalAccountBalance as number | string
              )}
            </p>
          </div>
          <div
            style={{ flex: "0 0 150px", textAlign: "right", paddingLeft: 10 }}
          >
            {showWarning || showZeroBalanceConfirmation ? (
              <OverlayTrigger
                rootClose
                trigger="click"
                placement="bottom"
                // @ts-expect-error popover is typed Element | null (the ternary
                // has a null branch) but OverlayTrigger only renders when
                // showWarning || showZeroBalanceConfirmation, so it is never
                // null here — inert react-bootstrap typing gap.
                overlay={popover}
              >
                <span>
                  {/* Button `name` is required in ButtonProps but optional in
                  practice (spread to <button name={undefined}>); original JS
                  omitted it — pre-existing inert prop omission. */}
                  {/* @ts-expect-error preserved missing required `name` prop */}
                  <Button
                    btnLabel={buttonState.label}
                    disabled={buttonState.disabled}
                  />
                </span>
              </OverlayTrigger>
            ) : (
              // @ts-expect-error Button `name` is required in ButtonProps but
              // optional in practice (spread to <button name={undefined}>);
              // original JS omitted it — pre-existing inert prop omission.
              <Button
                btnLabel={buttonState.label}
                onClick={() => this.handleCloseAccount(account)}
                disabled={buttonState.disabled}
              />
            )}
          </div>
        </div>
      );
    });
  };

  render() {
    return (
      <div className="close-account">
        <div className="page-header">
          <IconHeader
            variant="cardHeader"
            headerText="Close your Icon Account"
          />
          <IconSubheader subheader="Select an account to close. Cancelling your Icon account will put your IRA into a locked status. Once cancellation is finalized your IRA will be disbursed to you and any applicable taxes and fees will be applied." />
        </div>

        <div className="accounts-list mt-4">{this.renderAccountList()}</div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(CloseAccount));
